import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { verifyKioskDevice, submitKioskAbsensi } from '../../lib/api';
import * as faceapi from 'face-api.js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { 
  Smartphone, Users, Search, Loader2, Camera, Check, X,
  MapPin, Clock, ShieldCheck, ShieldAlert, AlertCircle, RefreshCw,
  ChevronRight, User, Building2, Scan
} from 'lucide-react';
import { toast } from '../ui/sonner';

// Face verification config
const FACE_CONFIG = {
  MIN_CONFIDENCE: 0.5,
  MODEL_URL: '/models',
};

const KioskPage = () => {
  const { deviceToken } = useParams();
  
  // Device & data states
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Step states: 'select' | 'verify' | 'success' | 'error'
  const [step, setStep] = useState('select');
  
  // Teacher selection
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [absenType, setAbsenType] = useState('masuk');
  
  // Face verification
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [stream, setStream] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [capturing, setCapturing] = useState(false);
  
  // Location
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Result
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  // Refs
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const overlayCanvasRef = React.useRef(null);
  const detectionIntervalRef = React.useRef(null);

  // Load device info and teachers
  useEffect(() => {
    loadDeviceInfo();
  }, [deviceToken]);

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setLoadingProgress(10);
        await faceapi.nets.tinyFaceDetector.loadFromUri(FACE_CONFIG.MODEL_URL);
        setLoadingProgress(40);
        await faceapi.nets.faceLandmark68Net.loadFromUri(FACE_CONFIG.MODEL_URL);
        setLoadingProgress(70);
        await faceapi.nets.faceRecognitionNet.loadFromUri(FACE_CONFIG.MODEL_URL);
        setLoadingProgress(100);
        setModelsLoaded(true);
      } catch (err) {
        console.error('Failed to load face models:', err);
      }
    };
    loadModels();
    
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, []);

  const loadDeviceInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await verifyKioskDevice(deviceToken);
      setDeviceInfo(data);
      setTeachers(data.teachers);
    } catch (err) {
      console.error('Device verification failed:', err);
      setError(err.response?.data?.detail || 'Device tidak valid atau tidak aktif');
    } finally {
      setLoading(false);
    }
  };

  // Get location
  const getLocation = useCallback(async () => {
    setGettingLocation(true);
    
    try {
      const { getCurrentPosition } = await import('../../lib/platformUtils');
      const position = await getCurrentPosition({ timeout: 30000 });
      setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp
      });
      setGettingLocation(false);
    } catch (err) {
      console.error('Location error:', err);
      toast.error('Gagal mendapatkan lokasi. Izinkan akses lokasi.');
      setGettingLocation(false);
    }
  }, []);

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      const { getCameraStream } = await import('../../lib/platformUtils');
      const mediaStream = await getCameraStream({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      });
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => {
            setCameraReady(true);
            startFaceDetection();
          });
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Gagal mengakses kamera');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraReady(false);
    setFaceDetected(false);
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
  }, [stream]);

  // Face detection loop
  const startFaceDetection = useCallback(() => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    detectionIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || !overlayCanvasRef.current || !modelsLoaded) return;

      const video = videoRef.current;
      const overlayCanvas = overlayCanvasRef.current;
      overlayCanvas.width = video.videoWidth;
      overlayCanvas.height = video.videoHeight;

      try {
        const detections = await faceapi
          .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions({
            inputSize: 320,
            scoreThreshold: FACE_CONFIG.MIN_CONFIDENCE
          }))
          .withFaceLandmarks();

        const ctx = overlayCanvas.getContext('2d');
        ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

        if (detections.length === 1) {
          setFaceDetected(true);
          const box = detections[0].detection.box;
          ctx.strokeStyle = '#10b981';
          ctx.lineWidth = 3;
          ctx.strokeRect(box.x, box.y, box.width, box.height);
        } else {
          setFaceDetected(false);
          if (detections.length > 1) {
            detections.forEach(det => {
              const box = det.detection.box;
              ctx.strokeStyle = '#ef4444';
              ctx.lineWidth = 2;
              ctx.strokeRect(box.x, box.y, box.width, box.height);
            });
          }
        }
      } catch (err) {
        console.error('Face detection error:', err);
      }
    }, 200);
  }, [modelsLoaded]);

  // Proceed to verification step
  const handleProceedToVerify = (teacher) => {
    setSelectedTeacher(teacher);
    
    // Determine absen type based on existing attendance
    if (teacher.absensi_hari_ini?.masuk && !teacher.absensi_hari_ini?.pulang) {
      setAbsenType('pulang');
    } else {
      setAbsenType('masuk');
    }
    
    setStep('verify');
    getLocation();
    
    // Initialize camera after short delay
    setTimeout(() => {
      if (modelsLoaded) {
        initCamera();
      }
    }, 300);
  };

  // Capture and submit
  const handleCaptureAndSubmit = async () => {
    if (!faceDetected || !selectedTeacher || !location) {
      if (!location) {
        toast.error('Lokasi belum tersedia. Mohon tunggu.');
        return;
      }
      toast.error('Pastikan wajah terdeteksi dengan jelas');
      return;
    }

    setCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Mirror for selfie
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0);

      const photoBase64 = canvas.toDataURL('image/jpeg', 0.8);

      // Get face descriptor
      const detections = await faceapi
        .detectSingleFace(canvas, new faceapi.TinyFaceDetectorOptions({
          inputSize: 416,
          scoreThreshold: FACE_CONFIG.MIN_CONFIDENCE
        }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detections) {
        toast.error('Tidak dapat mendeteksi wajah. Coba lagi.');
        setCapturing(false);
        return;
      }

      const faceDescriptor = Array.from(detections.descriptor);

      // Submit to server
      setSubmitting(true);
      stopCamera();

      const response = await submitKioskAbsensi({
        device_token: deviceToken,
        user_id: selectedTeacher.id,
        face_descriptor: faceDescriptor,
        foto_base64: photoBase64,
        latitude: location.latitude,
        longitude: location.longitude,
        akurasi_gps: location.accuracy,
        tipe: absenType
      });

      setResult(response.data);
      setStep('success');
      toast.success(response.message);

      // Auto reset after 5 seconds
      setTimeout(() => {
        handleReset();
      }, 5000);

    } catch (err) {
      console.error('Submit error:', err);
      const errorMsg = err.response?.data?.detail || 'Gagal melakukan absensi';
      toast.error(errorMsg);
      setResult({ error: errorMsg });
      setStep('error');
    } finally {
      setCapturing(false);
      setSubmitting(false);
    }
  };

  // Reset to initial state
  const handleReset = () => {
    stopCamera();
    setSelectedTeacher(null);
    setStep('select');
    setResult(null);
    setLocation(null);
    loadDeviceInfo(); // Refresh data
  };

  // Filter teachers
  const filteredTeachers = teachers.filter(t => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return t.nama.toLowerCase().includes(query) || t.nip_nuptk?.toLowerCase().includes(query);
  });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
            <p className="text-indigo-950 font-medium">Memuat data...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800 mb-2">Device Tidak Valid</h2>
            <p className="text-red-600">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Coba Lagi
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      {/* Header */}
      <div className="bg-indigo-950 text-white py-4 px-4 shadow-lg">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Smartphone className="w-6 h-6" />
            <div>
              <p className="font-semibold">{deviceInfo?.device_name}</p>
              <p className="text-xs text-indigo-200">{deviceInfo?.sekolah?.nama}</p>
            </div>
          </div>
          <Badge className="bg-emerald-500">Kiosk Mode</Badge>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4">
        {/* Step: Select Teacher */}
        {step === 'select' && (
          <div className="space-y-4">
            {/* Time Display */}
            <Card className="border-indigo-100/50 bg-white/80">
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-indigo-950">
                  {new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                </p>
                <p className="text-sm text-gray-500">
                  {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </CardContent>
            </Card>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Cari nama atau NIP..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-lg"
              />
            </div>

            {/* Teacher List */}
            <Card className="border-indigo-100/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Pilih Nama Anda ({filteredTeachers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 max-h-[50vh] overflow-y-auto">
                {filteredTeachers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>Tidak ada guru ditemukan</p>
                    <p className="text-sm">Pastikan wajah sudah didaftarkan</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredTeachers.map((teacher) => {
                      const hasAbsenMasuk = teacher.absensi_hari_ini?.masuk;
                      const hasAbsenPulang = teacher.absensi_hari_ini?.pulang;
                      const isComplete = hasAbsenMasuk && hasAbsenPulang;
                      
                      return (
                        <button
                          key={teacher.id}
                          onClick={() => !isComplete && handleProceedToVerify(teacher)}
                          disabled={isComplete}
                          className={`w-full flex items-center justify-between p-4 rounded-lg border text-left transition ${
                            isComplete 
                              ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed'
                              : 'bg-white border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isComplete ? 'bg-emerald-100' : 'bg-indigo-100'
                            }`}>
                              {isComplete ? (
                                <Check className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <User className="w-5 h-5 text-indigo-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-indigo-950">{teacher.nama}</p>
                              <p className="text-sm text-gray-500">{teacher.nip_nuptk}</p>
                              {(hasAbsenMasuk || hasAbsenPulang) && (
                                <div className="flex gap-2 mt-1">
                                  {hasAbsenMasuk && (
                                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                      Masuk ✓
                                    </Badge>
                                  )}
                                  {hasAbsenPulang && (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                                      Pulang ✓
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          {!isComplete && (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step: Face Verification */}
        {step === 'verify' && selectedTeacher && (
          <div className="space-y-4">
            {/* Selected Teacher Info */}
            <Card className="border-indigo-100/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="w-7 h-7 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-bold text-lg text-indigo-950">{selectedTeacher.nama}</p>
                    <p className="text-gray-500">{selectedTeacher.nip_nuptk}</p>
                    <Badge className={absenType === 'masuk' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                      Absen {absenType === 'masuk' ? 'Masuk' : 'Pulang'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Face Verification Camera */}
            <Card className="border-indigo-100/50 overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  Verifikasi Wajah
                </CardTitle>
                <CardDescription>
                  Posisikan wajah Anda di tengah layar
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {/* Camera Preview */}
                <div className="relative aspect-[4/3] bg-black">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  
                  {/* Face detection overlay */}
                  <canvas
                    ref={overlayCanvasRef}
                    className="absolute inset-0 w-full h-full"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  
                  {/* Hidden canvas for capture */}
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Loading overlay */}
                  {(!cameraReady || !modelsLoaded) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                      <div className="text-center text-white">
                        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2" />
                        <p className="text-sm">{!modelsLoaded ? 'Memuat model...' : 'Memuat kamera...'}</p>
                        {!modelsLoaded && (
                          <Progress value={loadingProgress} className="w-32 mx-auto mt-2" />
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Face detection status */}
                  <div className="absolute top-3 left-3">
                    <Badge 
                      className={faceDetected 
                        ? "bg-emerald-500 hover:bg-emerald-500" 
                        : "bg-gray-500 hover:bg-gray-500"
                      }
                    >
                      {faceDetected ? (
                        <>
                          <Check className="w-3 h-3 mr-1" />
                          Wajah Terdeteksi
                        </>
                      ) : (
                        <>
                          <Scan className="w-3 h-3 mr-1" />
                          Mencari Wajah...
                        </>
                      )}
                    </Badge>
                  </div>
                  
                  {/* Guide overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div 
                      className={`w-40 h-40 border-4 rounded-full transition-colors ${
                        faceDetected ? 'border-emerald-500/70' : 'border-white/30'
                      }`}
                      style={{ borderStyle: 'dashed' }}
                    />
                  </div>
                </div>

                {/* Location Status */}
                <div className="p-4 border-t bg-gray-50">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className={`w-4 h-4 ${location ? 'text-emerald-600' : 'text-amber-600'}`} />
                    {gettingLocation ? (
                      <span className="text-gray-500">Mendapatkan lokasi...</span>
                    ) : location ? (
                      <span className="text-emerald-700">Lokasi: Akurasi {Math.round(location.accuracy)}m</span>
                    ) : (
                      <span className="text-amber-700">Lokasi belum tersedia</span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-4 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleReset}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Batal
                  </Button>
                  <Button
                    onClick={handleCaptureAndSubmit}
                    disabled={!faceDetected || capturing || submitting || !location}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {capturing || submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 mr-2" />
                    )}
                    {submitting ? 'Memproses...' : 'Absen Sekarang'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && result && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">
                Absensi Berhasil!
              </h2>
              <p className="text-emerald-700 text-lg mb-4">{result.nama}</p>
              
              <div className="bg-white rounded-lg p-4 text-left space-y-2 mb-4">
                <p className="flex justify-between">
                  <span className="text-gray-500">Tipe:</span>
                  <span className="font-medium">{result.tipe === 'masuk' ? 'Masuk' : 'Pulang'}</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-500">Waktu:</span>
                  <span className="font-medium">
                    {new Date(result.waktu).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <Badge className={result.status === 'hadir_tepat_waktu' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {result.status === 'hadir_tepat_waktu' ? 'Tepat Waktu' : 'Terlambat'}
                  </Badge>
                </p>
                <p className="flex justify-between">
                  <span className="text-gray-500">Kecocokan Wajah:</span>
                  <span className="font-medium text-emerald-600">{result.face_match_score}%</span>
                </p>
              </div>
              
              <p className="text-sm text-emerald-600">Halaman akan reset dalam 5 detik...</p>
              
              <Button onClick={handleReset} variant="outline" className="mt-4">
                <RefreshCw className="w-4 h-4 mr-2" />
                Absensi Selanjutnya
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Error */}
        {step === 'error' && result?.error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <ShieldAlert className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-800 mb-2">
                Absensi Gagal
              </h2>
              <p className="text-red-700 mb-6">{result.error}</p>
              
              <Button onClick={handleReset} className="bg-red-600 hover:bg-red-700">
                <RefreshCw className="w-4 h-4 mr-2" />
                Coba Lagi
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default KioskPage;
